<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { Search, X } from 'lucide-svelte';

  import { contactMode, contactRoles, contactTitles } from '$lib/stores/selection';
  import { roleOptions } from '$lib/utils/roleMapping';
  import { scheduleCount } from '$lib/stores/guided';
  import { suggestTitles, type TitleSuggestion } from '$lib/api/titles';

  /**
   * Who at the company the customer wants to reach.
   *
   * # The order of the roles is a fallback chain, not a set
   *
   * This step treated the roles as an unordered multi-select, which threw away
   * half of what the filter means. `contact_roles` is an ordered preference:
   * `roleOrderCase` in searchrepo.go turns position into rank, and
   * `DISTINCT ON (company_id) ORDER BY company_id, <that rank>` picks the
   * best-ranked person each company actually has. Sending
   * ["operations", "marketing", "highest_decision_maker"] means: the head of
   * operations, or failing that marketing, or failing that whoever runs the
   * company.
   *
   * So the chain is what this collects, in the order it is clicked. The
   * sidebar expresses the same thing as a column of dropdowns; here it reads
   * as the sentence it is.
   *
   * The fallbacks do not change the running total — one person per company
   * either way, so the count is the companies employing anyone in the chain.
   * What they change is which person, which is why the total does not move
   * when the chain is reordered and should not be expected to.
   *
   * # Contact mode belongs here
   *
   * One person per company or everyone matching is a two-to-threefold swing in
   * what the customer pays, and in the app this replaces it was a setting they
   * met late, in a panel, beside things that cost nothing. Asked here, the
   * running total moves when it changes — which is the clearest possible
   * explanation of what it does. It is also what decides whether the fallback
   * chain means anything: with everyone matching, there is nothing to fall
   * back to.
   */
  const ordered = $derived($contactMode !== 'multiple');

  /**
   * The other way to name a person: by the title they hold.
   *
   * # Why it is a second way and not a second filter
   *
   * The departments cover the decision makers and stop there. Of the 222 people
   * whose title mentions Brandschutz, six carry a harmonized role — the rest
   * are invisible to the list above, and no amount of scrolling it will help.
   * So this asks the same question differently rather than adding a criterion:
   * the service uses the roles wherever they are set, and a customer who has
   * chosen a title is not also choosing a department.
   *
   * # The phrase proposes, the customer disposes
   *
   * A similarity floor cannot separate a job from the one beside it: "creative
   * director" reaches "art director" at 0.803, a different profession held by
   * 230 people, and no cut admits one without the other. So the titles are
   * shown with how many people hold each, ticked by default, and what stays
   * ticked is what the search uses.
   */
  let mode = $state<'roles' | 'titles'>($contactTitles.length > 0 ? 'titles' : 'roles');
  let phrase = $state('');
  let suggestions = $state<TitleSuggestion[]>([]);
  let searching = $state(false);
  let searched = $state(false);

  function useRoles() {
    mode = 'roles';
    if ($contactTitles.length > 0) {
      contactTitles.set([]);
      scheduleCount();
    }
  }

  function useTitles() {
    mode = 'titles';
    // The two are alternatives, so entering this one puts the chain down.
    if ($contactRoles.length > 0) {
      contactRoles.set([]);
      scheduleCount();
    }
  }

  /**
   * Titles the customer has taken off this list.
   *
   * Kept because searching again re-ticks what the phrase reaches, and the
   * search box keeps focus with the phrase still in it — so pressing Enter a
   * second time silently put back everything that had been unticked, and the
   * results then held people the customer had explicitly refused. The
   * decisions outlive the re-search; a new phrase is a new question and starts
   * clean.
   */
  let dismissed = $state(new Set<string>());
  let asked = $state('');

  async function runSearch() {
    if (!phrase.trim() || searching) return;

    const wanted = phrase.trim().toLowerCase();
    if (wanted !== asked) {
      dismissed = new Set();
      asked = wanted;
    }

    searching = true;
    try {
      suggestions = await suggestTitles(phrase);
      // Everything the phrase reached is ticked to begin with — minus whatever
      // has already been refused. The customer is here to remove what does not
      // belong, not to remove it twice.
      contactTitles.set(suggestions.map((s) => s.title).filter((t) => !dismissed.has(t)));
      searched = true;
      scheduleCount();
    } finally {
      searching = false;
    }
  }

  function toggleTitle(title: string) {
    const next = new Set(dismissed);
    if ($contactTitles.includes(title)) {
      next.add(title);
    } else {
      next.delete(title);
    }
    dismissed = next;

    contactTitles.update((titles) =>
      titles.includes(title) ? titles.filter((t) => t !== title) : [...titles, title]
    );
    scheduleCount();
  }

  const people = $derived(
    suggestions
      .filter((s) => $contactTitles.includes(s.title))
      .reduce((total, s) => total + s.people, 0)
  );

  function toggle(role: string) {
    contactRoles.update((roles) =>
      roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role]
    );
    scheduleCount();
  }

  function remove(role: string) {
    contactRoles.update((roles) => roles.filter((r) => r !== role));
    scheduleCount();
  }

  /** Move a link of the chain one place towards the front. */
  function promote(index: number) {
    if (index === 0) return;
    contactRoles.update((roles) => {
      const next = [...roles];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
    // No count: the chain decides which person, never how many.
  }

  function setMode(mode: 'single' | 'multiple') {
    contactMode.set(mode);
    scheduleCount();
  }
</script>

<div class="space-y-5">
  <!--
    Two ways to name a person, not two filters. Choosing one puts the other
    down, because the service uses the roles wherever they are set.
  -->
  <div class="inline-flex rounded-control bg-gray-100 p-0.5">
    {#each [{ id: 'roles' as const, go: useRoles }, { id: 'titles' as const, go: useTitles }] as way (way.id)}
      <button
        type="button"
        onclick={way.go}
        class="rounded-control px-3 py-1.5 text-sm font-medium transition-colors {mode === way.id
          ? 'bg-surface-panel text-gray-900'
          : 'text-gray-500 hover:text-gray-700'}"
        aria-pressed={mode === way.id}
      >
        {$_(`guided.role.by${way.id === 'roles' ? 'Department' : 'Title'}`)}
      </button>
    {/each}
  </div>

  <div>
    <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {$_('guided.role.howMany')}
    </p>
    <div class="inline-flex rounded-control bg-gray-100 p-0.5">
      {#each [{ mode: 'single' as const, key: 'one' }, { mode: 'multiple' as const, key: 'all' }] as option (option.mode)}
        <button
          type="button"
          onclick={() => setMode(option.mode)}
          class="rounded-control px-3 py-1.5 text-sm font-medium transition-colors {$contactMode ===
          option.mode
            ? 'bg-surface-panel text-gray-900 '
            : 'text-gray-500 hover:text-gray-700'}"
          aria-pressed={$contactMode === option.mode}
        >
          {$_(`guided.role.${option.key}Title`)}
        </button>
      {/each}
    </div>
    <p class="mt-1.5 text-xs text-gray-500">
      {$_(ordered ? 'guided.role.oneHint' : 'guided.role.allHint')}
    </p>
  </div>

  {#if mode === 'roles' && ordered && $contactRoles.length > 0}
    <div class="rounded-control border border-accent-200 bg-accent-50/60 p-3">
      <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-accent-800">
        {$_('guided.role.chain')}
      </p>

      <ol class="space-y-1.5">
        {#each $contactRoles as role, index (role)}
          <li class="flex items-center gap-2">
            <span
              class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-600 text-2xs font-semibold text-white"
            >
              {index + 1}
            </span>

            <span class="min-w-0 flex-1 truncate text-sm text-gray-900">
              {#if index > 0}
                <span class="text-gray-500">{$_('guided.role.orElse')}</span>
              {/if}
              {$_(`roles.${role}`)}
            </span>

            {#if index > 0}
              <button
                type="button"
                onclick={() => promote(index)}
                class="shrink-0 rounded px-1.5 py-0.5 text-xs font-medium text-accent-700 hover:bg-accent-100"
                title={$_('guided.role.promote')}
              >
                ↑
              </button>
            {/if}

            <button
              type="button"
              onclick={() => remove(role)}
              class="shrink-0 rounded p-1 text-gray-400 hover:bg-surface-panel hover:text-gray-700"
              aria-label={$_('guided.role.remove')}
            >
              <X class="h-3.5 w-3.5" />
            </button>
          </li>
        {/each}
      </ol>

      <p class="mt-2 text-xs text-accent-800/80">{$_('guided.role.chainHint')}</p>
    </div>
  {/if}

  {#if mode === 'titles'}
    <div class="space-y-3">
      <p class="text-xs text-gray-500">{$_('guided.role.titleHint')}</p>

      <form class="flex max-w-md gap-2" onsubmit={(e) => (e.preventDefault(), runSearch())}>
        <div class="relative flex-1">
          <Search
            class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            bind:value={phrase}
            placeholder={$_('guided.role.titlePlaceholder')}
            class="w-full rounded-control border border-hairline bg-white py-2 pl-9 pr-3 text-sm placeholder-gray-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
          />
        </div>
        <button
          type="submit"
          disabled={!phrase.trim() || searching}
          class="rounded-control bg-accent-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
        >
          {searching ? $_('status.loading') : $_('buttons.search')}
        </button>
      </form>

      {#if suggestions.length > 0}
        <!--
          Ticked to begin with, and shown with how many people hold each: the
          count is what tells somebody whether a title is worth keeping, and
          the largest group is often not the closest match.
        -->
        <ul class="grid gap-1.5 sm:grid-cols-2">
          {#each suggestions as suggestion (suggestion.title)}
            {@const kept = $contactTitles.includes(suggestion.title)}
            <li>
              <label
                class="flex cursor-pointer items-center gap-2.5 rounded-control border px-3 py-2 text-sm transition-colors {kept
                  ? 'border-accent-500 bg-accent-50'
                  : 'border-hairline bg-surface-panel hover:border-accent-300'}"
              >
                <input
                  type="checkbox"
                  checked={kept}
                  onchange={() => toggleTitle(suggestion.title)}
                  class="h-4 w-4 rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200"
                />
                <span class="min-w-0 flex-1 truncate text-gray-900">{suggestion.title}</span>
                <span class="shrink-0 text-2xs tabular-nums text-gray-500">{suggestion.people}</span
                >
              </label>
            </li>
          {/each}
        </ul>

        <p class="text-xs text-gray-500">
          {$_('guided.role.titleChosen', {
            values: { titles: $contactTitles.length, people }
          })}
        </p>
      {:else if searched && !searching}
        <p class="text-xs text-gray-500">{$_('guided.role.titleNothing')}</p>
      {/if}
    </div>
  {:else}
    <div>
      {#if ordered}
        <p class="mb-2 text-xs text-gray-500">
          {$contactRoles.length === 0
            ? $_('guided.role.pickFirst')
            : $_('guided.role.pickFallback')}
        </p>
      {/if}

      <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {#each roleOptions as role (role)}
          {@const at = $contactRoles.indexOf(role)}
          {@const selected = at >= 0}
          <button
            type="button"
            onclick={() => toggle(role)}
            class="flex items-center gap-2 rounded-control border px-3 py-2.5 text-left text-sm font-medium transition-colors {selected
              ? 'border-accent-500 bg-accent-50 text-accent-900'
              : 'border-hairline bg-surface-panel text-gray-900 hover:border-accent-300 hover:bg-gray-50'}"
            aria-pressed={selected}
          >
            {#if selected && ordered}
              <span
                class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-600 text-2xs font-semibold text-white"
              >
                {at + 1}
              </span>
            {/if}
            <span class="min-w-0 truncate">{$_(`roles.${role}`)}</span>
          </button>
        {/each}
      </div>
    </div>
  {/if}
</div>
